import { createContext, useContext } from "react";
import type { SiteConfig } from "./config";
import { siteConfig } from "./config";
import type { BusinessHours } from "@shared/businessHours";
import { DEFAULT_BUSINESS_HOURS } from "@shared/businessHours";
import type { BlogPlacement } from "@shared/blogPlacement";
import {
  DEFAULT_TESTIMONIAL_REQUEST_FLOW_SETTINGS,
  type TestimonialRequestBasics,
} from "@shared/testimonialRequestFlow";
import type { PublicLeadFormQuestions } from "@shared/leadFormQuestions";

// Live, user/DB-driven content for the hosted page. On /p/<slug> these arrays
// come from GET /api/ic/widget/:slug (the owner's database rows); on the
// design-preview route (no slug) they are mapped from the config.ts sample
// fixtures. The live page NEVER falls back to the sample data — empty arrays
// simply hide their section/tab.
export interface VaultMediaSlide {
  mediaAlt?: string | null;
  id: number | string;
  title: string;
  description: string;
  mediaUrl: string;
  // Per-slide image display: "fit" (whole image over the charcoal plate,
  // default) or "fill" (edge-to-edge crop). Videos ignore it.
  mediaFit?: "fit" | "fill";
  // Focal position (percent, 0–100) of the fill-mode crop — CSS
  // object-position. Undefined/absent = 50/50 (centered, the historical crop).
  mediaFocalX?: number;
  mediaFocalY?: number;
  // Zoom (percent, 100–300) of the fill-mode crop — CSS scale() about the
  // focal point. Undefined/absent = 100 (no zoom).
  mediaZoom?: number;
  // Button-action label/link. Used only when actionType is "button".
  ctaLabel?: string;
  ctaUrl?: string;
  secondaryCtaLabel?: string;
  secondaryCtaUrl?: string;
  actionType?: "none" | "button" | "session" | "form";
  actionSessionTypeId?: number | string | null;
  actionCampaignId?: number | string | null;
}

// A user-defined website tab: a name + its ordered slide-backed content.
export interface VaultTab {
  id: number | string;
  name: string;
  // Per-tab overrides for the tabbed section's vertical ghost watermark and
  // rotated stat badge. Blank = fall back to the site-wide values
  // (middleGhostWord chain / statChipValue+Label).
  ghostWord?: string;
  statChipValue?: string;
  statChipLabel?: string;
  slides: VaultMediaSlide[];
}

export interface VaultTeamMember {
  id: number | string;
  name: string;
  role: string;
  bio: string;
  mediaUrl: string;
}

export interface VaultTestimonial {
  id: number | string;
  name: string;
  detail: string;
  quote: string;
  mediaUrl: string;
  videoUrl: string;
}

export interface VaultFaq {
  id: number | string;
  question: string;
  answer: string;
}

export interface VaultContact {
  businessName: string;
  // Public Google Maps / Business Profile listing. The footer renders this as
  // one normal icon-row destination alongside the other social links.
  googleBusinessProfileUrl: string;
  // Footer ghost-watermark word (blank = fall back to businessName).
  footerWord: string;
  // Optional logo chosen specifically for the public footer watermark.
  // This is separate from the primary/header logo.
  footerLogoUrl: string;
  // Footer watermark mode: "logo" (faded logo, default) or "word" (ghost word).
  footerBrandMode: "none" | "logo" | "word";
  footerBrandOpacity: number;
  footerTaglineOpacity: number;
  footerAccentBurstVisibility: number;
  // Whether the tagline also renders under the footer watermark brand.
  footerShowTagline: boolean;
  // Footer booking button: shown at the end of the socials row when the
  // owner has a bookingUrl and hasn't turned it off; blank label = "Book Now".
  footerShowBookingButton: boolean;
  footerBookingLabel: string;
  footerCtaUrl: string;
  // Ignored legacy field; "Powered by InControl" always links to the homepage.
  referralCode: string;
  phone: string;
  // Footer phone-link behavior: "call" → tel: (default), "text" → sms:,
  // "both" → Call | Text links, "none" → plain text (no link).
  footerPhoneAction: "call" | "text" | "both" | "none";
  // Optional starter message pre-filled into the sms: link (when action "text").
  footerPhoneText: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  region: string;
  postalCode: string;
  country: string;
  socialLinks: string[];
  wideLogoUrl: string;
  // Short tagline under the business name in the page header (blank = none).
  tagline: string;
  // The owner's PRIMARY (light-background) logo (reads well on light cards),
  // rendered in the page header.
  logoUrl: string;
  // Owner-chosen display height (px) of the primary logo in the 80px header bar.
  headerLogoHeight: number;
  // Google-style hours of operation. Rendered in the footer when there's at
  // least one open day; hidden entirely when marked Not Applicable.
  businessHours: BusinessHours;
}

// Owner-chosen display labels for the "What to Expect" tabs. An empty string on
// any field means "use the built-in default label" (handled in Services.tsx).
export interface VaultTabLabels {
  services: string;
  gallery: string;
  team: string;
  testimonials: string;
  faqs: string;
  contact: string;
}

// Lead-capture config for the public "Contact" tab's Send a Message form. The
// require* flags mirror the owner's "info to collect" toggles (email is always
// collected); `confirmationMessage` is the success copy shown after a message
// is sent.
export interface VaultLeadCapture {
  requireFirstName: boolean;
  requireLastName: boolean;
  requirePhone: boolean;
  // Per-field REQUIRED flags — a field blocks submit only when it is BOTH
  // shown (above) AND required here (and left blank).
  firstNameRequired: boolean;
  lastNameRequired: boolean;
  phoneRequired: boolean;
  requireAddressLine1: boolean;
  requireAddressLine2: boolean;
  requireCity: boolean;
  requireRegion: boolean;
  requirePostalCode: boolean;
  // Per-address-field REQUIRED flags. A field blocks submit only when it is BOTH
  // shown (require* above) AND required here (and left blank).
  addressLine1Required: boolean;
  addressLine2Required: boolean;
  cityRequired: boolean;
  regionRequired: boolean;
  postalCodeRequired: boolean;
  confirmationMessage: string;
}

// Independent field picker for the website "Contact" tab's Send a Message form.
// Separate from VaultLeadCapture above — these flags drive ONLY the Contact
// form's rendered inputs + validation, never the hero lead-capture flow. Email
// + Message are always collected (not represented here). On = shown + required.
export interface VaultContactForm {
  firstName: boolean;
  lastName: boolean;
  phone: boolean;
  // Per-field REQUIRED flags for the simple fields — enforced only when shown.
  firstNameRequired: boolean;
  lastNameRequired: boolean;
  phoneRequired: boolean;
  addressLine1: boolean;
  addressLine2: boolean;
  city: boolean;
  region: boolean;
  postalCode: boolean;
  // Per-address-field REQUIRED flags. A field blocks submit only when it is BOTH
  // shown (above) AND required here (and left blank).
  addressLine1Required: boolean;
  addressLine2Required: boolean;
  cityRequired: boolean;
  regionRequired: boolean;
  postalCodeRequired: boolean;
}

// Standalone Contact copy (account-level business_profile) driving the public
// footer "Contact Us" modal. Blank fields fall back to built-in defaults in the
// Contact modal component.
export interface VaultContactCopy {
  headline: string;
  intro: string;
  ctaLabel: string;
  // Post-submit "thank you" copy. Blank = DEFAULT_FORM_CONFIRMATION fallback.
  confirmation: string;
}

// Public-safe campaign configuration referenced by a slide form action. Its
// visible fields come from this campaign's require* flags (email is always
// collected). `id` is sent on form submission for campaign attribution.
export interface VaultSpecial {
  id: number | string;
  name: string;
  campaignType: string;
  offerVideoUrl: string;
  // Image display for the offer media: "fit" letterboxes the whole image over
  // the charcoal plate (default), "fill" crops edge-to-edge. Videos always fit.
  mediaFit?: "fit" | "fill";
  // Focal position (percent, 0–100) of the fill-mode crop — CSS
  // object-position. Undefined/absent = 50/50 (centered).
  mediaFocalX?: number;
  mediaFocalY?: number;
  mediaZoom?: number;
  cardIntro: string;
  ctaLabel: string;
  // Per-campaign "After They Submit" success copy (After They Submit tab).
  // Blank falls back to the template's default confirmation line.
  confirmationMessage: string;
  // Done-phase media (image or video) swapped into the slide's media frame
  // once the visitor submits. Blank = keep showing the offer media.
  afterMediaUrl?: string;
  // Optional done-phase CTA button under the confirmation copy. Blank label =
  // no button; URL is external or an internal "slide:"/"campaign:" token.
  afterCtaLabel?: string;
  afterCtaUrl?: string;
  requireFirstName: boolean;
  requireLastName: boolean;
  requirePhone: boolean;
  // Per-field REQUIRED flags — a field blocks submit only when it is BOTH
  // shown (above) AND required here (and left blank).
  firstNameRequired: boolean;
  lastNameRequired: boolean;
  phoneRequired: boolean;
  requireAddressLine1: boolean;
  requireAddressLine2: boolean;
  requireCity: boolean;
  requireRegion: boolean;
  requirePostalCode: boolean;
  // Per-address-field REQUIRED flags. A field blocks submit only when it is BOTH
  // shown (require* above) AND required here (and left blank).
  addressLine1Required: boolean;
  addressLine2Required: boolean;
  cityRequired: boolean;
  regionRequired: boolean;
  postalCodeRequired: boolean;
  /** Visitor-safe questions; server payload intentionally omits destinations. */
  questionConfig?: PublicLeadFormQuestions;
}

export interface VaultContent {
  /** Published-only blog listing for the movable Blog tab. */
  blog?: import("@shared/blog").PublicBlogPage | null;
  blogLabel?: string;
  blogPlacement: BlogPlacement;
  /** True only when the owner has configured all BYO Twilio credentials. */
  // User-defined website tabs (Task 1132) — the source of truth for the public
  // "What to Expect" section. Rendered in order, each through MediaShowcase.
  tabs: VaultTab[];
  // Ordered user-defined tab keys. Legacy built-in section keys are ignored;
  // migration moves their content into tabs.
  navOrder: string[];
  // Retained for older payload compatibility; slide-backed rendering ignores it.
  sessionsVisible: boolean;
  // Legacy labels retained for older payload compatibility; not rendered as
  // separate middle sections.
  offersLabel: string;
  feedbackLabel: string;
  sessionsLabel: string;
  // Stat badge pinned to the tabbed section's slide card (rotated accent chip,
  // e.g. "500+" / "Members Coached"). Blank value = no badge rendered.
  statChipValue: string;
  statChipLabel: string;
  // Fixed word for the tabbed section's vertical ghost watermark. Blank =
  // fall back to contact.footerWord, then contact.businessName (Services.tsx).
  middleGhostWord: string;
  middleBackgroundColor: string;
  middleHideSingleTab: boolean;
  middleBackgroundUrl: string;
  middleBackgroundGlow: number;
  middleCardGlow: number;
  middleWordOpacity: number;
  pagesBackgroundColor: string;
  pagesBackgroundUrl: string;
  pagesBackgroundGlow: number;
  pagesBackgroundOpacity: number;
  blogBackgroundColor: string;
  blogBackgroundUrl: string;
  blogBackgroundGlow: number;
  blogBackgroundOpacity: number;
  // Legacy built-in section styling retained for compatibility.
  offersGhostWord: string;
  offersStatValue: string;
  offersStatLabel: string;
  feedbackGhostWord: string;
  feedbackStatValue: string;
  feedbackStatLabel: string;
  sessionsGhostWord: string;
  sessionsStatValue: string;
  sessionsStatLabel: string;
  // Online booking link — the owner's scheduler URL (Settings → Online
  // Booking). Non-blank = "booking" CTA tokens render as a new-tab link to
  // it. Blank = booking buttons hidden (feature off).
  bookingUrl: string;
  // Booking pill/CTA target (Task 2143): "external" scheduler link (default)
  // or "schedule" — in-page navigation to the class-schedule section.
  // Combined with bookingUrl + classSchedule via resolveBookingCta
  // (shared/footerModel.ts) so every booking button on the page agrees.
  bookingTarget: string;
  // Dormant legacy fixed-section content retained for payload compatibility.
  services: VaultMediaSlide[];
  gallery: VaultMediaSlide[];
  team: VaultTeamMember[];
  testimonials: VaultTestimonial[];
  faqs: VaultFaq[];
  tabLabels: VaultTabLabels;
  contact: VaultContact;
  leadCapture: VaultLeadCapture;
  contactForm: VaultContactForm;
  contactCopy: VaultContactCopy;
  // Digital Receptionist (Task 1491) — the owner-chosen receptionist name
  // shown in the public chat widget's header (blank = generic label).
  receptionist: {
    name: string;
    enabled?: boolean;
    launcherText: string;
    launcherSubtext: string;
    launcherImageUrl?: string;
    launcherImageZoom?: number;
    launcherImageFocalX?: number;
    launcherImageFocalY?: number;
    contactForm: VaultContactForm;
    confirmation: string;
  };
  specials: VaultSpecial[];
  // Legacy feedback source rows. Public Services renders migrated equivalents
  // only when they are included in a user-defined tab.
  clientFeedback: VaultMediaSlide[];
  // Legacy offer source rows; public Services never synthesizes a standalone tab.
  offerSlides: VaultMediaSlide[];
  // Legacy session source rows; migration places them in ordinary tabs.
  sessionSlides: VaultMediaSlide[];
  // Hero media override — the main hero video/image at the top of the page, or
  // null to keep the template's default hero media. mediaKind is "image" |
  // "video" | "" (empty when unknown; the renderer detects by extension).
  // mediaFit/mediaFocalX/Y: fill-mode focal position — same contract as the
  // other slide surfaces (fill = object-cover cropped at the owner's focal
  // point; fit = letterboxed, focal ignored).
  heroSlide: {
    id?: number;
    ctaLabel?: string;
    ctaUrl?: string;
    secondaryCtaLabel?: string;
    secondaryCtaUrl?: string;
    actionType?: VaultMediaSlide["actionType"];
    actionSessionTypeId?: number | null;
    actionCampaignId?: number | null;
    mediaUrl: string;
    mediaAlt?: string | null;
    mediaKind: string;
    mediaFit?: "fit" | "fill";
    mediaFocalX?: number;
    mediaFocalY?: number;
    mediaZoom?: number;
    eyebrow?: string | null;
    highlightPhrase?: string | null;
    backgroundColor?: string;
    backgroundImageUrl?: string;
    backgroundImageOpacity?: number;
  } | null;
  // When true (the default), the final slide of the Client Feedback carousel is
  // a "submit your own testimonial" form — but only when at least one published
  // testimonial already exists. Off hides the form slide entirely.
  enableTestimonialSubmission: boolean;
  testimonialCopy: TestimonialRequestBasics;
  // Legacy schedule data retained for migration/other integrations; Services
  // does not render a standalone schedule.
  classSchedule: VaultClassEntry[];
  // Session offering data referenced by slide session actions.
  sessionTypes: VaultSessionType[];
}

export interface VaultClassEntry {
  id: number;
  title: string;
  description: string;
  dayOfWeek: number;
  startTime: string; // "HH:MM" 24h, owner-local
  durationMinutes: number;
  rsvpEnabled: boolean;
  nextDate: string; // "YYYY-MM-DD" next occurrence
  spotsLeft: number | null; // null when RSVP is off
  // Per-date cancellation (Task 2148): the next occurrence is cancelled —
  // rendered struck-through with the note; Save-my-spot suppressed.
  cancelled?: boolean;
  cancelNote?: string;
  // Per-date move (Task 2167): the next occurrence starts at this time
  // instead of the weekly startTime ("" / absent = normal week).
  movedStartTime?: string;
  movedNote?: string;
  // 1-on-1 booking slot (Task 2672): rendered in the separate "Available
  // Sessions" list. Server only sends these while unclaimed.
  isOneOnOne?: boolean;
}

// One slot inside a VaultSessionType — a single recurring time occurrence.
export interface VaultSessionTypeSlot {
  id: number;
  dayOfWeek: number;
  startTime: string; // "HH:MM" — the recurring weekly start time
  durationMinutes: number;
  rsvpEnabled: boolean;
  // nextDate is present only for RSVP-enabled slots (server emits it after
  // next-occurrence resolution).  Non-RSVP (informational) slots have no
  // nextDate — they are schedule-display only, with no booking flow.
  nextDate?: string; // "YYYY-MM-DD" — next occurrence date (RSVP slots only)
  // Full group occurrences keep nextDate so an existing reservation can be
  // released, but must not offer a new RSVP. Never exposes reservation counts.
  full?: boolean;
  // When the server emits a per-date move for the next occurrence (i.e.
  // buildPublicSessionTypes found a matching ScheduleException with
  // newStartTime), this holds the effective start time for that occurrence
  // only.  Absent or empty = no move, the weekly startTime applies.
  effectiveStartTime?: string; // "HH:MM" — only present when moved
}

// A Session Type (Task 2724): a named group of recurring schedule slots.
// Public website shows one media card per type; clicking a CTA opens a slot
// picker (the existing RSVP/release flow against a real entry id + date).
export interface VaultSessionType {
  id: number;
  title: string;
  description: string;
  // Determines the offering's public behavior. This is intentionally not
  // inferred from its editable title: consultation CTAs must keep working
  // after an owner renames the card.
  sessionPurpose: "regular" | "consultation";
  // "group" = class/group session; "one-on-one" = individual slot.
  classification: "group" | "one-on-one";
  // Optional media for the type's website card.
  mediaUrl: string;
  mediaFit?: "fit" | "fill";
  mediaFocalX?: number;
  mediaFocalY?: number;
  mediaZoom?: number;
  // All recurring time slots under this type.
  slots: VaultSessionTypeSlot[];
}

export const EMPTY_VAULT_CONTENT: VaultContent = {
  blog: null,
  blogPlacement: "tab",
  tabs: [],
  navOrder: [],
  sessionsVisible: true,
  offersLabel: "",
  feedbackLabel: "",
  sessionsLabel: "",
  statChipValue: "",
  statChipLabel: "",
  middleGhostWord: "",
  middleBackgroundColor: "#FFFFFF",
  middleHideSingleTab: false,
  middleBackgroundUrl: "",
  middleBackgroundGlow: 100,
  middleCardGlow: 100,
  middleWordOpacity: 8,
  pagesBackgroundColor: "#FFFFFF",
  pagesBackgroundUrl: "",
  pagesBackgroundGlow: 0,
  pagesBackgroundOpacity: 32,
  blogBackgroundColor: "#FFFFFF",
  blogBackgroundUrl: "",
  blogBackgroundGlow: 0,
  blogBackgroundOpacity: 32,
  offersGhostWord: "",
  offersStatValue: "",
  offersStatLabel: "",
  feedbackGhostWord: "",
  feedbackStatValue: "",
  feedbackStatLabel: "",
  sessionsGhostWord: "",
  sessionsStatValue: "",
  sessionsStatLabel: "",
  bookingUrl: "",
  bookingTarget: "external",
  services: [],
  gallery: [],
  team: [],
  testimonials: [],
  faqs: [],
  tabLabels: { services: "", gallery: "", team: "", testimonials: "", faqs: "", contact: "" },
  contact: {
    businessName: "",
    footerWord: "",
    footerLogoUrl: "",
    footerBrandMode: "logo",
    footerBrandOpacity: 50,
    footerTaglineOpacity: 50,
    footerAccentBurstVisibility: 0,
    footerShowTagline: true,
    footerShowBookingButton: true,
    footerBookingLabel: "",
    footerCtaUrl: "receptionist",
    referralCode: "",
    phone: "",
    footerPhoneAction: "call",
    footerPhoneText: "",
    addressLine1: "",
    addressLine2: "",
    city: "",
    region: "",
    postalCode: "",
    country: "",
    socialLinks: [],
    googleBusinessProfileUrl: "",
    wideLogoUrl: "",
    tagline: "",
    logoUrl: "",
    headerLogoHeight: 40,
    businessHours: DEFAULT_BUSINESS_HOURS,
  },
  leadCapture: {
    requireFirstName: true,
    requireLastName: false,
    requirePhone: false,
    firstNameRequired: false,
    lastNameRequired: false,
    phoneRequired: false,
    requireAddressLine1: false,
    requireAddressLine2: false,
    requireCity: false,
    requireRegion: false,
    requirePostalCode: false,
    addressLine1Required: true,
    addressLine2Required: false,
    cityRequired: false,
    regionRequired: false,
    postalCodeRequired: false,
    confirmationMessage: "",
  },
  contactForm: {
    firstName: true,
    lastName: false,
    phone: false,
    firstNameRequired: false,
    lastNameRequired: false,
    phoneRequired: false,
    addressLine1: false,
    addressLine2: false,
    city: false,
    region: false,
    postalCode: false,
    addressLine1Required: true,
    addressLine2Required: false,
    cityRequired: false,
    regionRequired: false,
    postalCodeRequired: false,
  },
  contactCopy: {
    headline: "",
    intro: "",
    ctaLabel: "",
    confirmation: "",
  },
  receptionist: { name: "", launcherText: "Instant Answers", launcherSubtext: "from Our Digital Receptionist", launcherImageZoom: 100, launcherImageFocalX: 50, launcherImageFocalY: 50, contactForm: {
    firstName: true, lastName: false, phone: true, firstNameRequired: false, lastNameRequired: false,
    phoneRequired: false, addressLine1: false, addressLine2: false, city: false, region: false,
    postalCode: false, addressLine1Required: true, addressLine2Required: false, cityRequired: false,
    regionRequired: false, postalCodeRequired: false,
  }, confirmation: "" },
  specials: [],
  clientFeedback: [],
  offerSlides: [],
  sessionSlides: [],
  heroSlide: null,
  enableTestimonialSubmission: true,
  testimonialCopy: { ...DEFAULT_TESTIMONIAL_REQUEST_FLOW_SETTINGS.basics },
  classSchedule: [],
  sessionTypes: [],
};

interface SiteConfigContextValue {
  config: SiteConfig;
  content: VaultContent;
  publicHomeHref?: string;
  // The raw GET /api/ic/widget/:slug payload, present ONLY when the page was
  // server-rendered/seeded (the SSR'd hosted page). Hero reads it to seed its
  // own `cfg` synchronously so the server markup + the client's first render
  // agree (no fetch flash, no hydration mismatch). Undefined on the preview
  // route and on SPA-nav into /p/:slug, where Hero keeps its own fetch path.
  widgetPayload?: unknown;
}

const SiteConfigContext = createContext<SiteConfigContextValue>({
  config: siteConfig as SiteConfig,
  content: EMPTY_VAULT_CONTENT,
});

export function SiteConfigProvider({
  config,
  content,
  widgetPayload,
  publicHomeHref = "/",
  children,
}: {
  config: SiteConfig;
  content: VaultContent;
  widgetPayload?: unknown;
  publicHomeHref?: string;
  children: React.ReactNode;
}) {
  return (
    <SiteConfigContext.Provider value={{ config, content, widgetPayload, publicHomeHref }}>
      {children}
    </SiteConfigContext.Provider>
  );
}

export function useSiteConfig() {
  return useContext(SiteConfigContext);
}
